import { httpClient } from '@/shared/api';
import type {
  SchoolSizeCostApiParams,
  SchoolSizeCostBandSummaryRow,
  SchoolSizeCostDistribution,
  SchoolSizeCostEfficiencyStatus,
  SchoolSizeCostFilterParams,
  SchoolSizeCostResult,
  SchoolSizeCostSchoolRow,
  SchoolSizeCostSummary,
  SchoolSizeCostTrendPoint,
} from './schoolSizeCost.dto';

const PREFIX = '/app/school-size-cost/school-size-costs';

function readNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function readStr(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function unwrapPayload(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if ('result' in o && o.result != null && typeof o.result === 'object') {
    return o.result as Record<string, unknown>;
  }
  if ('data' in o && o.data != null && typeof o.data === 'object') {
    return o.data as Record<string, unknown>;
  }
  return o;
}

function normalizeEfficiencyStatus(raw: unknown): SchoolSizeCostEfficiencyStatus {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (raw === 1) return 'high_cost_for_size';
    if (raw === 2) return 'low_cost_for_size';
    return 'efficient';
  }
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === 'highcostforsize' || s === 'high_cost_for_size' || s.includes('high')) {
    return 'high_cost_for_size';
  }
  if (s === 'lowcostforsize' || s === 'low_cost_for_size' || s.includes('low')) {
    return 'low_cost_for_size';
  }
  return 'efficient';
}

function mapDistribution(raw: unknown): SchoolSizeCostDistribution {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    min: readNum(o, ['min', 'Min']) ?? 0,
    p25: readNum(o, ['p25', 'P25']) ?? 0,
    median: readNum(o, ['median', 'Median']) ?? 0,
    p75: readNum(o, ['p75', 'P75']) ?? 0,
    max: readNum(o, ['max', 'Max']) ?? 0,
  };
}

function mapSummaryFromRoot(o: Record<string, unknown>): SchoolSizeCostSummary {
  return {
    schoolCount: readNum(o, ['schoolInScope', 'SchoolInScope', 'schoolCount', 'SchoolCount']) ?? 0,
    medianCostPerStudentUsd:
      readNum(o, ['medianCostPerStudent', 'MedianCostPerStudent', 'medianCostPerStudentUsd', 'MedianCostPerStudentUsd']) ?? 0,
    medianTuitionPerStudentUsd:
      readNum(o, ['medianTuitionPerStudent', 'MedianTuitionPerStudent', 'medianTuitionPerStudentUsd', 'MedianTuitionPerStudentUsd']) ?? 0,
    highCostForSizeCount:
      readNum(o, ['highCostForSize', 'HighCostForSize', 'highCostForSizeCount', 'HighCostForSizeCount']) ?? 0,
    lowCostForSizeCount:
      readNum(o, ['lowCostForSize', 'LowCostForSize', 'lowCostForSizeCount', 'LowCostForSizeCount']) ?? 0,
    medianEnrollment: readNum(o, ['medianEnrollment', 'MedianEnrollment']) ?? 0,
  };
}

function mapSummaryNested(raw: unknown): SchoolSizeCostSummary {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    schoolCount: readNum(o, ['schoolCount', 'SchoolCount', 'schoolInScope', 'SchoolInScope']) ?? 0,
    medianCostPerStudentUsd:
      readNum(o, ['medianCostPerStudentUsd', 'MedianCostPerStudentUsd', 'medianCostPerStudent', 'MedianCostPerStudent']) ?? 0,
    medianTuitionPerStudentUsd:
      readNum(o, ['medianTuitionPerStudentUsd', 'MedianTuitionPerStudentUsd', 'medianTuitionPerStudent', 'MedianTuitionPerStudent']) ?? 0,
    highCostForSizeCount:
      readNum(o, ['highCostForSizeCount', 'HighCostForSizeCount', 'highCostForSize', 'HighCostForSize']) ?? 0,
    lowCostForSizeCount:
      readNum(o, ['lowCostForSizeCount', 'LowCostForSizeCount', 'lowCostForSize', 'LowCostForSize']) ?? 0,
    medianEnrollment: readNum(o, ['medianEnrollment', 'MedianEnrollment']) ?? 0,
  };
}

function mapRow(raw: unknown): SchoolSizeCostSchoolRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const schoolId = readStr(o, ['schoolId', 'SchoolId']);
  if (!schoolId) return null;

  return {
    schoolId,
    schoolCode: readStr(o, ['schoolCode', 'SchoolCode']),
    schoolName: readStr(o, ['schoolName', 'SchoolName']),
    governorateId: readNum(o, ['governorateId', 'GovernorateId', 'governerateId', 'GovernerateId']) ?? 0,
    governorateName: readStr(o, ['governorateName', 'GovernorateName', 'governerateName', 'GovernerateName']),
    cityName: readStr(o, ['cityName', 'CityName']),
    areaName: readStr(o, ['areaName', 'AreaName']),
    studentsCount: readNum(o, ['studentsCount', 'StudentsCount']) ?? 0,
    schoolSizeBandId: readStr(o, ['schoolSizeBandId', 'SchoolSizeBandId']),
    schoolSizeBandName: readStr(o, ['schoolSizeBandName', 'SchoolSizeBandName']),
    costPerStudentUsd:
      readNum(o, ['costPerStudentUsd', 'CostPerStudentUsd', 'costPerStudent', 'CostPerStudent']) ?? 0,
    tuitionPerStudentUsd:
      readNum(o, ['tuitionPerStudentUsd', 'TuitionPerStudentUsd', 'tuitionPerStudent', 'TuitionPerStudent']) ?? 0,
    surplusPerStudentUsd:
      readNum(o, ['surplusPerStudentUsd', 'SurplusPerStudentUsd', 'surplusPerStudent', 'SurplusPerStudent']) ?? 0,
    expectedCostPerStudentUsd:
      readNum(o, [
        'expectedCostPerStudentUsd',
        'ExpectedCostPerStudentUsd',
        'expectedCostPerStudent',
        'ExpectedCostPerStudent',
      ]) ?? 0,
    varianceVsScalePct: readNum(o, ['varianceVsScalePct', 'VarianceVsScalePct']) ?? 0,
    efficiencyStatus: normalizeEfficiencyStatus(o.efficiencyStatus ?? o.EfficiencyStatus),
  };
}

function mapBandSummary(raw: unknown): SchoolSizeCostBandSummaryRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const schoolSizeBandId = readStr(o, ['schoolSizeBandId', 'SchoolSizeBandId']);
  if (!schoolSizeBandId) return null;

  return {
    schoolSizeBandId,
    schoolSizeBandName: readStr(o, ['schoolSizeBandName', 'SchoolSizeBandName']),
    schoolsCount: readNum(o, ['schoolsCount', 'SchoolsCount']) ?? 0,
    medianStudents: readNum(o, ['medianStudents', 'MedianStudents']) ?? 0,
    medianCostPerStudentUsd:
      readNum(o, ['medianCostPerStudentUsd', 'MedianCostPerStudentUsd', 'medianCostPerStudent', 'MedianCostPerStudent']) ?? 0,
    medianTuitionPerStudentUsd:
      readNum(o, ['medianTuitionPerStudentUsd', 'MedianTuitionPerStudentUsd', 'medianTuitionPerStudent', 'MedianTuitionPerStudent']) ?? 0,
    medianSurplusPerStudentUsd:
      readNum(o, ['medianSurplusPerStudentUsd', 'MedianSurplusPerStudentUsd', 'medianSurplusPerStudent', 'MedianSurplusPerStudent']) ?? 0,
    nationalBandMedianCostUsd:
      readNum(o, [
        'nationalBandMedianCostUsd',
        'NationalBandMedianCostUsd',
        'nationalBandMedianCost',
        'NationalBandMedianCost',
      ]) ?? 0,
    varianceVsNationalBandPct: readNum(o, ['varianceVsNationalBandPct', 'VarianceVsNationalBandPct']) ?? 0,
  };
}

function mapTrendPoint(raw: unknown): SchoolSizeCostTrendPoint | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  return {
    studentsCount: readNum(o, ['studentsCount', 'StudentsCount']) ?? 0,
    expectedCostPerStudentUsd:
      readNum(o, [
        'expectedCostPerStudentUsd',
        'ExpectedCostPerStudentUsd',
        'expectedCostPerStudent',
        'ExpectedCostPerStudent',
      ]) ?? 0,
    schoolSizeBandName: readStr(o, ['schoolSizeBandName', 'SchoolSizeBandName']),
  };
}

export function mapSchoolSizeCostResult(raw: unknown): SchoolSizeCostResult {
  const o = unwrapPayload(raw) ?? {};

  const nestedSummary = o.summary ?? o.Summary;
  const summary =
    nestedSummary != null && typeof nestedSummary === 'object'
      ? mapSummaryNested(nestedSummary)
      : mapSummaryFromRoot(o);

  const rawRows = (o.rows ?? o.Rows) as unknown[] | undefined;
  const rows = Array.isArray(rawRows)
    ? rawRows.map(mapRow).filter((r): r is SchoolSizeCostSchoolRow => r != null)
    : [];

  const rawBands = (o.bandSummaries ?? o.BandSummaries) as unknown[] | undefined;
  const bandSummaries = Array.isArray(rawBands)
    ? rawBands.map(mapBandSummary).filter((b): b is SchoolSizeCostBandSummaryRow => b != null)
    : [];

  const rawTrend = (o.trendLine ?? o.TrendLine) as unknown[] | undefined;
  const trendLine = Array.isArray(rawTrend)
    ? rawTrend.map(mapTrendPoint).filter((p): p is SchoolSizeCostTrendPoint => p != null)
    : [];

  return {
    summary,
    distribution: mapDistribution(o.distribution ?? o.Distribution),
    rows,
    bandSummaries,
    trendLine,
    scaleInsight: readStr(o, ['scaleInsight', 'ScaleInsight']),
  };
}

function buildQuery(params: SchoolSizeCostApiParams): string {
  const q = new URLSearchParams();
  q.set('Year', String(params.year));
  if (params.governorateId != null && Number.isFinite(params.governorateId)) {
    q.set('GovernorateId', String(params.governorateId));
  }
  if (params.cityId != null && Number.isFinite(params.cityId)) {
    q.set('CityId', String(params.cityId));
  }
  if (params.areaId != null && Number.isFinite(params.areaId)) {
    q.set('AreaId', String(params.areaId));
  }
  if (params.schoolSizeBandId?.trim()) {
    q.set('SchoolSizeBandId', params.schoolSizeBandId.trim());
  }
  return q.toString();
}

export function filterParamsToSchoolSizeCostApiParams(
  params: SchoolSizeCostFilterParams
): SchoolSizeCostApiParams | null {
  const year = parseInt(params.year.trim(), 10);
  if (!Number.isFinite(year)) return null;

  const apiParams: SchoolSizeCostApiParams = { year };

  if (params.governorateId != null && Number.isFinite(params.governorateId)) {
    apiParams.governorateId = params.governorateId;
  }
  if (params.cityId != null && Number.isFinite(params.cityId)) {
    apiParams.cityId = params.cityId;
  }
  if (params.areaId != null && Number.isFinite(params.areaId)) {
    apiParams.areaId = params.areaId;
  }
  if (params.schoolSizeBandId?.trim()) {
    apiParams.schoolSizeBandId = params.schoolSizeBandId.trim();
  }

  return apiParams;
}

export const schoolSizeCostApi = {
  getSchoolSizeCosts: async (params: SchoolSizeCostApiParams): Promise<SchoolSizeCostResult> => {
    const query = buildQuery(params);
    const res = await httpClient.get<unknown>(`${PREFIX}?${query}`);
    return mapSchoolSizeCostResult(res);
  },
};
